import type { NoteAttachment } from "@/types/notes";
import type {
  AttachmentStudioState,
  PhotoStudioSettings,
  PhotoStudioSettingsUpdate,
  StudioBackdrop,
  StudioRender,
  StudioRenderBatchRequest,
  StudioRenderSize,
} from "@/types/photo-studio";

import { requestData } from "@/api/api-config";

export async function getPhotoStudioSettings() {
  return await requestData<PhotoStudioSettings>("photo-studio/settings", "get");
}

export async function updatePhotoStudioSettings(
  update: PhotoStudioSettingsUpdate,
) {
  return await requestData<PhotoStudioSettings>(
    "photo-studio/settings",
    "put",
    { json: update },
  );
}

export async function getAttachmentStudio(attachmentId: string) {
  return await requestData<AttachmentStudioState>(
    `photo-studio/attachments/${attachmentId}`,
    "get",
  );
}

export async function setAttachmentBackdrop(
  attachmentId: string,
  backdrop: StudioBackdrop | null,
) {
  return await requestData<AttachmentStudioState>(
    `photo-studio/attachments/${attachmentId}`,
    "put",
    { json: { backdrop } },
  );
}

// `mask`: PNG, grey value in R=G=B (255 = person, 0 = background), fully opaque.
export async function uploadStudioMask(
  attachmentId: string,
  mask: Blob,
  expectedGcsPath: string,
) {
  const formData = new FormData();
  formData.append("file", mask, "mask.png");
  return await requestData<AttachmentStudioState>(
    `photo-studio/attachments/${attachmentId}/mask`,
    "put",
    { body: formData, searchParams: { expectedGcsPath } },
  );
}

export async function requestStudioRefine(attachmentId: string) {
  return await requestData<AttachmentStudioState>(
    `photo-studio/attachments/${attachmentId}/refine`,
    "post",
  );
}

export async function getStudioRender(
  attachmentId: string,
  options: { backdrop?: StudioBackdrop; size?: StudioRenderSize } = {},
) {
  return await requestData<StudioRender>(
    `photo-studio/attachments/${attachmentId}/render`,
    "get",
    { searchParams: { ...options } },
  );
}

export async function getStudioRenders(request: StudioRenderBatchRequest) {
  return await requestData<StudioRender[]>(
    "photo-studio/renders/batch",
    "post",
    { json: request },
  );
}

export async function revertAttachmentToOriginal(attachmentId: string) {
  return await requestData<NoteAttachment>(
    `photo-studio/attachments/${attachmentId}/revert-original`,
    "post",
  );
}
